import { Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, ActivatedRoute } from '@angular/router';
import { CONSTANT_VALUES } from './../constants/constant-values';
import { HandleSideMenuWithAuth } from './message.event';

@Injectable()
export class AuthGuard implements CanActivate {
    // Route -> roles mapping
    private authGuard: { [route: string]: string[] } = {
        'signup': [CONSTANT_VALUES.SUPER_ADMIN],
        'facility': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'addfacility': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'staff-list': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],

        'addevent': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'addlink': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'links': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'link-broadcast': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'event-broadcast': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'events': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],

        'add-group-booking': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'group-booking-broadcast': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'group-bookings': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],

        'addmember': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'member': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'calendar': [CONSTANT_VALUES.ROLE_STAFF, CONSTANT_VALUES.ROLE_VENDOR_ADMIN, CONSTANT_VALUES.SUPER_ADMIN],
        'logout': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN, CONSTANT_VALUES.ROLE_STAFF, CONSTANT_VALUES.SUPER_ADMIN],
        'creategroup': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'disablemember': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'memberdetail': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'activemember': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'feedback': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'group': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'managegroup': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'feedbacklist': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'broadcast': [CONSTANT_VALUES.SUPER_ADMIN, CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'alert': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'feed': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'vendor-profile': [CONSTANT_VALUES.ROLE_STAFF, CONSTANT_VALUES.ROLE_VENDOR_ADMIN, CONSTANT_VALUES.SUPER_ADMIN],
        'homeaccount': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN],
        'loyalty': [CONSTANT_VALUES.ROLE_VENDOR_ADMIN, CONSTANT_VALUES.ROLE_STAFF, CONSTANT_VALUES.SUPER_ADMIN],
    };

    // Make sure these keys match actual vendor.role values
    private defaultRouteForRole: { [role: string]: string } = {
        [CONSTANT_VALUES.ROLE_VENDOR_ADMIN]: 'member',
        [CONSTANT_VALUES.ROLE_STAFF]: 'calendar',
        [CONSTANT_VALUES.SUPER_ADMIN]: 'facility'
    };

    // Routes controlled by feature flags
    private featureRouteMap: { [route: string]: string } = {
        'feedbacklist': 'isFeedbackEnabled',

        'events': 'isEventEnabled',
        'addevent': 'isEventEnabled',
        'event-broadcast': 'isEventEnabled',

        'links': 'isLinksEnabled',
        'addlink': 'isLinksEnabled',
        'link-broadcast': 'isLinksEnabled',

        'add-group-booking': 'isGroupClassesEnabled',
        'group-bookings': 'isGroupClassesEnabled',
        'group-booking-broadcast': 'isGroupClassesEnabled',
    };

    constructor(
        private router: Router,
        public handleSideMenuWithAuth: HandleSideMenuWithAuth,
        private route: ActivatedRoute
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        if (this.sessionCheck() && this.authenticateUser()) {
            return true;
        } else {
            this.router.navigate(['/']);
            return false;
        }
    }

    private tryGetVendor(): any | null {
        const vendorData = localStorage.getItem('vendor');
        if (!vendorData) return null;
        try {
            return JSON.parse(atob(vendorData));
        } catch (e) {
            return null;
        }
    }

    sessionCheck() {
        const vendorData = localStorage.getItem('vendor');
        if (vendorData && (() => { try { atob(vendorData); return true; } catch(e) { return false; } })()) {
            return true;
        } else {
            let source: string | undefined;
            this.route.queryParams.subscribe(params => { source = params['source']; });
            this.router.navigate(['/'], { queryParams: { source } });
            return false;
        }
    }

    private canAccessRoute(route: string, vendor: any): boolean {
        const flagKey = this.featureRouteMap[route];
        if (!flagKey) return true;               // route not gated by a feature flag
        if (!vendor) return false;               // no vendor -> deny
        return !!vendor[flagKey];                // must be truthy
    }

    authenticateUser(): boolean {
        const vendor = this.tryGetVendor();
        console.log('Authenticating user', vendor);
        if (!vendor || !vendor.role) {
            return false;
        }
        const location = window.location;

        const route = location.pathname.replace('/', '');

        this.renderRoleWiseAppMenu(vendor.role);

        // Feature flag gate
        if (!this.canAccessRoute(route, vendor)) {
            return false;
        }

        // Role gate
        const allowedRoles = this.authGuard[route];
        if (Array.isArray(allowedRoles)) {
            return allowedRoles.indexOf(vendor.role) > -1;
        }
        return true; // no rule => allow
    }

    navigateUserToDefaultRoute() {
        const vendor = this.tryGetVendor();
        if (!vendor) { this.router.navigate(['/']); return; }
        const target = this.defaultRouteForRole[vendor.role] || '/';
        this.router.navigate(['/' + target]);
    }

    renderRoleWiseAppMenu(role: string) {
        const vendor = this.tryGetVendor();
        if (!vendor) return;

        let allowLoyalty = false;
        if (vendor.bottomMenu && vendor.bottomMenu.loyalty) {
            allowLoyalty = vendor.bottomMenu.loyalty;
        }

        this.handleSideMenuWithAuth.fire({
            fire: 'on',
            role: role,
            isFeedbackEnabled: vendor.isFeedbackEnabled,
            isEventEnabled: vendor.isEventEnabled,
            isLinksEnabled: vendor.isLinksEnabled,
            isGroupClassesEnabled: vendor.isGroupClassesEnabled,
            allowPayment: vendor.allowPayment,
            allowLoyalty: allowLoyalty
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/auth-guard.ts